import { useQuery } from '@tanstack/react-query';

import { api } from '../lib/api-client';

import type { TenantStatus, PackageType } from '../features/admin/tenant-lifecycle/types';
import type { TenantLifecycleSnapshotWire } from '../lib/api-client';

export interface TenantLifecycleDashboardResult {
  items: TenantLifecycleSnapshotWire[];
  nextCursor: string | null;
  totals: Partial<Record<TenantStatus, number>>;
}

export interface TenantLifecycleDashboardParams {
  status?: TenantStatus;
  packageType?: PackageType;
  cursor?: string;
  limit?: number;
}

/**
 * useTenantLifecycleDashboard — React Query para GET /v1/tenants/lifecycle/dashboard.
 *
 * Consome capability `tenant-lifecycle-read` (Pro+ — gestor + admin_tenant).
 * staleTime 60s — dados de lifecycle mudam raramente em tempo real.
 *
 * W3.3 Sprint Frontend Desktop — T13 Tenant Lifecycle Dashboard.
 */
export function useTenantLifecycleDashboard(params: TenantLifecycleDashboardParams = {}): {
  data: TenantLifecycleDashboardResult | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: ['tenant-lifecycle', 'dashboard', params],
    queryFn: async () => {
      const apiParams: {
        status?: string;
        packageType?: string;
        cursor?: string;
        limit?: number;
      } = {};
      if (params.status !== undefined) apiParams.status = params.status;
      if (params.packageType !== undefined) apiParams.packageType = params.packageType;
      if (params.cursor !== undefined) apiParams.cursor = params.cursor;
      if (params.limit !== undefined) apiParams.limit = params.limit;
      const result = await api.getTenantLifecycleDashboard(apiParams);
      return result;
    },
    staleTime: 60 * 1000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}
